import CustomTooltip from '../../../shared/CustomTooltip';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import {
    Avatar,
    Box,
    Chip,
    Grid,
    lighten,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { openReportAPIRefSliceAction } from '../../../../app/Slices/ReportAPIRefSlice';
import UserRole from '../../../../shared/AuthRoles';
import { capitalizeName } from '../../../../shared/utils';
import { OwnerRole } from '../../../enums/users';
import GenericTable, { LinkType } from '../../../shared/GenericTable';
import BulkActions from './BulkActions/BulkActions';
import WaitListPreview from './WaitListPreview';
import { sevenDayApi } from '../../../../app/Slices/SevenDayApiSlice';
import { AppDispatch } from '../../../../app/Slices/store';
import { theme } from '../../../../shared/theme';
import { useSearchParams } from 'react-router';

interface ReservationListProps {
    tab?: string;
    reservations: any;
    pageSize?: number;
    searchValue?: string;
}
const safeLower = (v: any) => String(v ?? '').toLowerCase();

const isWaitlistTab = (tab?: string) => {
    const t = String(tab ?? '').toLowerCase();
    return t === 'waitlist' || t === 'w/list archive';
};

const getPrimaryUser = (row: any, tab?: string) => {
    if (isWaitlistTab(tab)) return row?.createdBy ?? row?.booker ?? row?.guest;
    return row?.booker ?? row?.guest ?? row?.createdBy;
};

const getPrimaryName = (row: any, tab?: string) => {
    const p = getPrimaryUser(row, tab);
    const first = p?.firstName ?? '';
    const last = p?.lastName ?? '';
    const full = `${first} ${last}`.trim();
    return full || row?.guest?.name || row?.owner?.name || '-';
};

const getPrimaryEmail = (row: any, tab?: string) => {
    const p = getPrimaryUser(row, tab);
    return p?.email ?? row?.bookingInfo?.email ?? '-';
};

const getPrimaryAvatar = (row: any, tab?: string) => {
    const p = getPrimaryUser(row, tab);

    return (
        p?.profilePicture?.url ??
        p?.profilePicture?.location ??
        p?.profilePicture?.src ??
        p?.profilePictureUrl ??
        p?.avatarUrl ??
        p?.avatar ??
        p?.photoUrl ??
        row?.booker?.profilePicture?.url ??
        row?.guest?.profilePicture?.url ??
        row?.createdBy?.profilePicture?.url ??
        ''
    );
};

const getCompanyName = (row: any, tab?: string) => {
    if (isWaitlistTab(tab)) return row?.guest?.name ?? '';
    return (
        row?.guest?.name ??
        row?.owner?.name ??
        row?.company?.name ??
        row?.bookingInfo?.companyName ??
        row?.bookingInfo?.company ??
        ''
    );
};

const getModuleName = (row: any) =>
    row?.module?.name ?? row?.listing?.name ?? '-';

const getModuleType = (row: any) => {
    const t = row?.module?.type ?? row?.moduleType ?? row?.listing?.type;
    return t ? capitalizeName(t) : '-';
};

const getCheckinTime = (row: any) => {
    return row?.bookingInfo?.checkinTime
        ? moment(row.bookingInfo.checkinTime, 'HH:mm').format('h:mm A')
        : '-';
};

const getInitials = (fullName: string) => {
    const parts = String(fullName ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (!parts.length) return '?';

    const first = parts[0]?.[0] ?? '';
    const last = (parts.length > 1 ? parts[parts.length - 1]?.[0] : '') ?? '';
    const initials = (first + last).toUpperCase();

    return initials || '?';
};

export default function ViewReservationListComponent({
    tab,
    reservations,
    pageSize = 100,
    searchValue,
}: ReservationListProps) {
    const loggedInUser = UserRole();
    const [showWaitListModal, setShowWaitListModal] = useState(false);
    const [waitListId, setWaitListId] = useState(0);
    const [isEditingWaitList, setIsEditingWaitList] = useState(false);
    const [selected, setSelected] = useState<number[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const globalFilter =
        searchValue ?? sessionStorage.getItem('searchValue') ?? '';

    const [searchParams, setSearchParams] = useSearchParams();

    const apiRef = useGridApiRef();
    const dispatch = useDispatch<AppDispatch>();

    const processedData = useMemo(() => {
        if (!reservations) return [];
        return reservations.map((row: any) => ({
            ...row,
            startDate: row?.startDate
                ? new Date(new Date(row.startDate).setHours(0, 0, 0, 0))
                : row?.startDate,
            endDate: row?.endDate
                ? new Date(new Date(row.endDate).setHours(0, 0, 0, 0))
                : row?.endDate,
            createdAt: row?.createdAt
                ? new Date(new Date(row.createdAt).setHours(0, 0, 0, 0))
                : row?.createdAt,
        }));
    }, [reservations]);

    const q = String(globalFilter ?? '')
        .trim()
        .toLowerCase();

    const getReservedByValue = (row: any, tab?: string) => {
        const primaryName = getPrimaryName(row, tab);
        const primaryEmail = getPrimaryEmail(row, tab);
        const companyName = getCompanyName(row, tab);
        const ownerName = row?.owner?.name ?? '';
        const createdByName =
            `${row?.createdBy?.firstName ?? ''} ${row?.createdBy?.lastName ?? ''}`.trim();
        const bookerName =
            `${row?.booker?.firstName ?? ''} ${row?.booker?.lastName ?? ''}`.trim();

        return [
            primaryName,
            primaryEmail,
            companyName,
            ownerName,
            createdByName,
            bookerName,
            row?.id ?? '',
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
    };

    const filteredData = useMemo(() => {
        if (!q) return processedData;

        return processedData.filter((r: any) => {
            const primary = getReservedByValue(r, tab);
            const module =
                `${getModuleName(r)} ${getModuleType(r)} ${r?.owner?.name ?? ''}`.toLowerCase();
            const ref = String(r?.id ?? '').toLowerCase();
            const status = String(r?.status ?? '').toLowerCase();
            const visit = String(
                r?.bookingInfo?.visitType ?? 'blocked',
            ).toLowerCase();

            return (
                primary.includes(q) ||
                module.includes(q) ||
                ref.includes(q) ||
                status.includes(q) ||
                visit.includes(q)
            );
        });
    }, [processedData, q, tab]);

    useEffect(() => {
        const v = String(searchValue ?? '').trim();
        if (!apiRef?.current?.setQuickFilterValues) return;
        apiRef.current.setQuickFilterValues(v ? [v] : []);
    }, [apiRef, searchValue]);

    useEffect(() => {
        const csv = (apiRef.current as any)?.exportDataAsCsv;
        const print = (apiRef.current as any)?.exportDataAsPrint;
        const search = (apiRef.current as any)?.setQuickFilterValues;

        dispatch(
            openReportAPIRefSliceAction({
                exportInfo: {
                    csvExport: typeof csv === 'function' ? csv : undefined,
                    printExport:
                        typeof print === 'function' ? print : undefined,
                    search: typeof search === 'function' ? search : undefined,
                },
            }),
        );
    }, [apiRef, dispatch]);

    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isLaptop = useMediaQuery('(max-width: 1600px)');
    const isDesktop = useMediaQuery('(max-width: 2300px)');

    const statusSx = (s: string) => {
        switch (s) {
            case 'approved':
                return {
                    bgcolor: lighten(theme.palette.yellow.main, 0.78),
                    color: theme.palette.yellow.main,
                    borderColor: lighten(theme.palette.yellow.main, 0.35),
                };
            case 'pending':
                return {
                    bgcolor: lighten(theme.palette.primary.main, 0.76),
                    color: theme.palette.primary.main,
                    borderColor: lighten(theme.palette.primary.main, 0.32),
                };
            case 'cancelled':
            case 'declined':
                return {
                    bgcolor: lighten(theme.palette.error.main, 0.82),
                    color: theme.palette.error.main,
                    borderColor: lighten(theme.palette.error.main, 0.3),
                };
            case 'completed':
                return {
                    bgcolor: lighten(theme.palette.info.main, 0.82),
                    color: theme.palette.info.main,
                    borderColor: lighten(theme.palette.info.main, 0.3),
                };
            default:
                return {
                    bgcolor: theme.palette.custom.cardActionBackground,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.custom.lightBorderColor,
                };
        }
    };

    const getCheckoutTime = (row: any) => {
        return row?.bookingInfo?.checkoutTime
            ? moment(row.bookingInfo.checkoutTime, 'HH:mm').format('h:mm A')
            : '-';
    };

    const avatarSx = {
        width: 40,
        height: 40,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        '& .MuiSvgIcon-root': {
            display: 'block',
        },
    };

    const columns: MRT_ColumnDef<any>[] = useMemo(
        () => [
            {
                accessorKey: 'primaryGuest',
                header: 'Primary Guest',
                filterFn: 'fuzzy',
                size: isMobile ? 150 : isIpad ? 180 : 200,
                maxSize: 220,
                minSize: 170,
                accessorFn: (row) => getReservedByValue(row, tab),
                Cell: ({ row }) => {
                    const r: any = row.original;
                    const name = getPrimaryName(r, tab);
                    const email = getPrimaryEmail(r, tab);
                    const company = getCompanyName(r, tab);
                    const avatarUrl = getPrimaryAvatar(r, tab);
                    const initials = getInitials(name);

                    return (
                        <Stack
                            direction='row'
                            spacing={1.25}
                            alignItems='center'
                            sx={{ minWidth: 0 }}
                        >
                            <Avatar
                                src={avatarUrl || undefined}
                                sx={avatarSx}
                                imgProps={{
                                    referrerPolicy: 'no-referrer',
                                    onError: (e) => {
                                        (
                                            e.currentTarget as HTMLImageElement
                                        ).src = '';
                                    },
                                }}
                            >
                                {avatarUrl ? null : initials}
                            </Avatar>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {name}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {email}
                                </Typography>

                                <Stack
                                    direction='row'
                                    spacing={1}
                                    alignItems='center'
                                    sx={{ mt: 0.25, minWidth: 0 }}
                                >
                                    {!isMobile && (
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {company}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    );
                },
            },
            {
                accessorKey: 'id',
                header: 'Ref',
                size: 80,
                maxSize: 90,
                minSize: 70,
                filterFn: (row, columnId, filterValue) => {
                    const value = row.getValue<number>(columnId);
                    return value?.toString().includes(filterValue);
                },
                Cell: ({ row }) => {
                    const r: any = row.original;

                    const reservationRef = r?.id ? `#${r.id}` : '-';
                    const recurringId =
                        r?.recurringId ?? r?.recurringReservationId ?? null;

                    const recurringRef = recurringId ? `#${recurringId}` : null;

                    return (
                        <Stack spacing={0.25}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                {reservationRef}
                            </Typography>

                            {recurringRef && (
                                <CustomTooltip
                                    title='This reservation is part of a recurring reservation with multiple bookings.'
                                    arrow
                                    placement='top'
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            cursor: 'help',
                                        }}
                                    >
                                        Recurring: {recurringRef}
                                    </Typography>
                                </CustomTooltip>
                            )}
                        </Stack>
                    );
                },
            },
            {
                accessorKey: 'module',
                header: 'Experience',
                filterFn: 'fuzzy',
                size: isMobile ? 180 : isIpad ? 200 : 220,
                maxSize: 260,
                minSize: 170,
                accessorFn: (row) =>
                    `${getModuleName(row)} ${getModuleType(row)}`,
                Cell: ({ row }) => {
                    const r: any = row.original;

                    const moduleAvatar =
                        r?.module?.photo ??
                        r?.module?.image?.src ??
                        r?.module?.image?.url ??
                        r?.module?.coverImage?.src ??
                        r?.module?.coverImage?.url ??
                        '';

                    const isUnavailable = r?.module?.isAvailable === false;

                    if (isUnavailable) {
                        return (
                            <Stack
                                direction='row'
                                spacing={1}
                                alignItems='center'
                                sx={{ minWidth: 0 }}
                            >
                                <Avatar
                                    sx={{
                                        ...avatarSx,
                                        bgcolor:
                                            theme.palette.custom
                                                .cardActionBackground,
                                        color: theme.palette.text.secondary,
                                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                    }}
                                >
                                    <DoDisturbAltIcon sx={{ fontSize: 18 }} />
                                </Avatar>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {getModuleName(r)}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        This experience is no longer active
                                    </Typography>
                                </Box>
                            </Stack>
                        );
                    }

                    const moduleFallback = String(getModuleName(r) || '?')
                        .trim()
                        .charAt(0)
                        .toUpperCase();

                    return (
                        <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                            sx={{ minWidth: 0 }}
                        >
                            <Avatar
                                src={moduleAvatar || undefined}
                                sx={avatarSx}
                                imgProps={{
                                    referrerPolicy: 'no-referrer',
                                    onError: (e) => {
                                        (
                                            e.currentTarget as HTMLImageElement
                                        ).src = '';
                                    },
                                }}
                            >
                                {moduleAvatar ? null : moduleFallback}
                            </Avatar>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {getModuleName(r)}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    Host: {r.owner?.name}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {getModuleType(r)}
                                </Typography>
                            </Box>
                        </Stack>
                    );
                },
            },
            {
                accessorKey: 'visitType',
                header: 'Visit type',
                size: isMobile ? 50 : isIpad ? 60 : 60,
                maxSize: 70,
                minSize: 50,
                filterVariant: 'select',
                mantineFilterSelectProps: {
                    data: [
                        { label: 'Business', value: 'business' },
                        { label: 'Personal', value: 'personal' },
                    ],
                    clearable: true,
                },
                Cell: ({ cell }) => (
                    <Box sx={{ pt: 0.25 }}>
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {String(cell.getValue<string>() ?? '')}
                        </Typography>
                    </Box>
                ),
                accessorFn: (row) =>
                    row.bookingInfo?.visitType
                        ? capitalizeName(row.bookingInfo.visitType)
                        : 'Blocked',
            },
            {
                accessorKey: 'startDate',
                header: 'Check-in',
                size: 120,
                filterVariant: 'date',
                filterFn: 'greaterThanOrEqualTo',
                sortingFn: 'datetime',
                mantineFilterDateInputProps: { valueFormat: 'll' },
                Cell: ({ row, cell }) => {
                    const date = moment(cell.getValue<Date>()).format('ll');
                    const time = getCheckinTime(row.original);

                    return (
                        <Box sx={{ pt: 0.25 }}>
                            <Stack spacing={0.25}>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {date}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {time}
                                </Typography>
                            </Stack>
                        </Box>
                    );
                },
            },
            {
                accessorKey: 'endDate',
                header: 'Checkout',
                size: 120,
                filterVariant: 'date',
                filterFn: 'lessThanOrEqualTo',
                sortingFn: 'datetime',
                mantineFilterDateInputProps: { valueFormat: 'll' },
                Cell: ({ row, cell }) => {
                    const date = moment(cell.getValue<Date>()).format('ll');
                    const time = getCheckoutTime(row.original);

                    return (
                        <Box sx={{ pt: 0.25 }}>
                            <Stack spacing={0.25}>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {date}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {time}
                                </Typography>
                            </Stack>
                        </Box>
                    );
                },
            },
            {
                accessorKey: 'status',
                header: 'Status',
                enableColumnFilter: false,
                size: 80,
                maxSize: 100,
                minSize: 50,
                Cell: ({ cell }) => {
                    const value = safeLower(cell.getValue<string>());
                    return (
                        <Box
                            sx={{
                                pt: 0.25,
                                display: 'flex',
                                justifyContent: 'flex-start',
                            }}
                        >
                            <Chip
                                label={capitalizeName(value)}
                                variant='outlined'
                                size='small'
                                sx={{
                                    width: 72,
                                    justifyContent: 'center',
                                    '& .MuiChip-label': {
                                        width: '100%',
                                        textAlign: 'center',
                                        px: 0.5,
                                        pt: 0.2,
                                    },
                                    ...statusSx(value),
                                    cursor: 'pointer',
                                }}
                            />
                        </Box>
                    );
                },
            },
        ],
        [isMobile, isIpad, isLaptop, isDesktop, tab, loggedInUser?.role],
    );

    const columnVisibility = {
        owner:
            loggedInUser?.role === 'guest_admin' ||
            loggedInUser?.role === 'guest_department_admin' ||
            loggedInUser?.role === 'guest_user',
        recurringId:
            loggedInUser?.role.includes('owner') && !isWaitlistTab(tab),
        onBehalf: loggedInUser?.role.includes('owner') && !isWaitlistTab(tab),
        visitType: !isWaitlistTab(tab),
    };

    const handleRowClick = (data: any) => {
        if (isWaitlistTab(tab)) {
            setWaitListId(data.id);
            setShowWaitListModal(true);
            setSearchParams({ selectedWaitlist: data.id });

            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('wait-list-request', String(data.id));
                return next;
            });
        }
    };

    useEffect(() => {
        const waitlistId = searchParams.get('wait-list-request');
        if (!waitlistId) return;

        const parsed = Number(waitlistId);
        if (!Number.isFinite(parsed)) return;

        setWaitListId(parsed);
        setShowWaitListModal(true);
    }, [searchParams]);

    const handleCloseModal = () => {
        setShowWaitListModal(false);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('wait-list-request');
            return next;
        });
        setWaitListId(0);
        setTimeout(() => {
            setIsEditingWaitList(false);
        }, 200);
    };

    const getSelectedRowId = (row: any) => {
        const candidate =
            row?.original?.id ??
            row?._valuesCache?.id ??
            (typeof row?.getValue === 'function'
                ? row.getValue('id')
                : undefined) ??
            row?.id;

        const n = Number(candidate);
        return Number.isFinite(n) ? n : null;
    };

    const handleSelectionChange = useCallback(
        (rows: any[]) => {
            if (loggedInUser?.role !== OwnerRole.Admin) return;

            const ids = rows
                .map((r) => getSelectedRowId(r))
                .filter((v): v is number => typeof v === 'number');

            setSelected(ids);
            setSelectedRows(rows.map((r) => r?.original ?? r).filter(Boolean));
        },
        [loggedInUser?.role],
    );

    return (
        <Grid sx={{ paddingBottom: 10 }}>
            <Grid
                sx={{
                    paddingBottom: 10,
                    '& td': { verticalAlign: 'top' },
                    '& td .mantine-Table-td': { verticalAlign: 'top' },
                    '& td > div': { alignItems: 'flex-start' },
                    '& td > div > div:not(.MuiAvatar-root)': {
                        alignItems: 'flex-start',
                    },
                    '& .MuiAvatar-root': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                    '& .MuiTablePagination-toolbar': {
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexWrap: 'nowrap',
                        gap: 1,
                        minHeight: '56px',
                    },
                    '& .MuiTablePagination-spacer': {
                        flex: 1,
                    },
                    '& .MuiTablePagination-selectLabel': {
                        margin: 0,
                        whiteSpace: 'nowrap',
                    },
                    '& .MuiTablePagination-input': {
                        margin: 0,
                    },
                    '& .MuiTablePagination-displayedRows': {
                        margin: 0,
                        whiteSpace: 'nowrap',
                    },
                    '& .MuiTablePagination-actions': {
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 0,
                    },
                    '& .MuiPagination-root': {
                        display: 'flex',
                        alignItems: 'center',
                    },
                    '& .MuiPagination-ul': {
                        display: 'flex',
                        flexDirection: 'row !important',
                        flexWrap: 'nowrap !important',
                        alignItems: 'center',
                        gap: 0.5,
                        margin: 0,
                        padding: 0,
                    },
                    '& .mantine-Pagination-root': {
                        display: 'flex',
                        alignItems: 'center',
                        width: 'auto',
                    },
                    '& .mantine-Pagination-control': {
                        flexShrink: 0,
                    },
                    '& .mantine-Pagination-dots': {
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    },
                    '& .mantine-Pagination-list': {
                        display: 'flex !important',
                        flexDirection: 'row !important',
                        flexWrap: 'nowrap !important',
                        alignItems: 'center',
                        gap: 6,
                        margin: '0 !important',
                        padding: '0 !important',
                        listStyle: 'none',
                    },
                    '& .mantine-Pagination-item': {
                        display: 'inline-flex',
                        flexShrink: 0,
                    },
                    '@media (max-width: 770px)': {
                        '& .MuiTablePagination-toolbar': {
                            paddingLeft: 8,
                            paddingRight: 8,
                            justifyContent: 'space-between',
                        },
                        '& .MuiTablePagination-selectLabel': {
                            fontSize: 12,
                        },
                        '& .MuiTablePagination-displayedRows': {
                            fontSize: 12,
                        },
                        '& .MuiPagination-ul': {
                            gap: 4,
                        },
                        '& .mantine-Pagination-list': {
                            gap: 4,
                        },
                    },
                }}
            >
                <GenericTable
                    columns={columns}
                    data={filteredData}
                    tableName='7DAY-Reservations'
                    loading={!reservations}
                    linkType={
                        !isWaitlistTab(tab) ? LinkType.Reservation : undefined
                    }
                    columnVisibility={columnVisibility}
                    onSelectionChange={handleSelectionChange}
                    onRowClick={handleRowClick}
                    cursor='pointer'
                    sorting={
                        isWaitlistTab(tab)
                            ? [{ id: 'id', desc: true }]
                            : undefined
                    }
                    state={{
                        globalFilter: searchValue ?? '',
                    }}
                    onGlobalFilterChange={(v) => {
                        const next = String(v ?? '');
                        sessionStorage.setItem('searchValue', next);
                    }}
                    enableTopToolbar={false}
                    enableToolbarInternalActions={false}
                    positionGlobalFilter={undefined}
                />
            </Grid>

            {waitListId > 0 && (
                <WaitListPreview
                    id={waitListId}
                    open={showWaitListModal}
                    onClose={handleCloseModal}
                    isEditingWaitList={isEditingWaitList}
                    setIsEditingWaitList={setIsEditingWaitList}
                />
            )}

            <BulkActions
                selected={selected}
                selectedRows={selectedRows}
                onClearSelection={() => {
                    setSelected([]);
                    setSelectedRows([]);
                }}
            />
        </Grid>
    );
}
